import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, KeyRound, Mail, Download, Trash2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Account = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user, getAuthHeaders, updateUser, logout } = useAuth();

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const [emailForm, setEmailForm] = useState({ newEmail: "", password: "" });
  const [savingEmail, setSavingEmail] = useState(false);

  const [exporting, setExporting] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate("/login");
  }, [authLoading, isAuthenticated, navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { headers: getAuthHeaders() });
      toast.success("Password updated successfully");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPw(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const res = await axios.post(`${API}/auth/change-email`,
        { new_email: emailForm.newEmail, password: emailForm.password },
        { headers: getAuthHeaders() });
      updateUser({ email: res.data.email });
      toast.success("Email updated successfully");
      setEmailForm({ newEmail: "", password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/auth/export-data`, { headers: getAuthHeaders() });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revivedu-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (err) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (deleteConfirm !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: getAuthHeaders(),
        data: { password: deletePassword }
      });
      toast.success("Account deleted");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button data-testid="account-back-btn" variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6 rounded-full">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-secondary mb-2">Account Settings</h1>
          <p className="text-lg text-foreground/80">Signed in as {user?.email}</p>
        </div>

        <div className="space-y-8">
          {/* Change Password */}
          <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="change-password-card">
            <CardHeader>
              <CardTitle className="text-2xl text-secondary flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change Password</CardTitle>
              <CardDescription>Update the password you use to sign in</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="current-password" className="mb-2 block font-medium">Current password</Label>
                  <div className="relative">
                    <Input id="current-password" data-testid="current-password-input" type={showPw ? "text" : "password"} value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className="h-12 rounded-xl border-2 pr-12" placeholder="Leave blank if you signed up with Google" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/50 hover:text-secondary" aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-account-password-btn">
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="new-password" className="mb-2 block font-medium">New password</Label>
                  <Input id="new-password" data-testid="new-password-input" type={showPw ? "text" : "password"} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className="h-12 rounded-xl border-2" required />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="mb-2 block font-medium">Confirm new password</Label>
                  <Input id="confirm-password" data-testid="confirm-new-password-input" type={showPw ? "text" : "password"} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="h-12 rounded-xl border-2" required />
                </div>
                <Button type="submit" disabled={savingPw} data-testid="save-password-btn" className="rounded-full bg-primary hover:bg-primary/90">
                  {savingPw ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : "Update Password"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Email */}
          <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="change-email-card">
            <CardHeader>
              <CardTitle className="text-2xl text-secondary flex items-center gap-2"><Mail className="h-5 w-5" /> Change Email</CardTitle>
              <CardDescription>Your current email is {user?.email}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangeEmail} className="space-y-4">
                <div>
                  <Label htmlFor="new-email" className="mb-2 block font-medium">New email</Label>
                  <Input id="new-email" data-testid="new-email-input" type="email" value={emailForm.newEmail} onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })} className="h-12 rounded-xl border-2" placeholder="you@example.com" required />
                </div>
                <div>
                  <Label htmlFor="email-password" className="mb-2 block font-medium">Confirm with password</Label>
                  <Input id="email-password" data-testid="email-password-input" type="password" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} className="h-12 rounded-xl border-2" placeholder="Leave blank if you signed up with Google" />
                </div>
                <Button type="submit" disabled={savingEmail} data-testid="save-email-btn" className="rounded-full bg-primary hover:bg-primary/90">
                  {savingEmail ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : "Update Email"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Download data */}
          <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="export-data-card">
            <CardHeader>
              <CardTitle className="text-2xl text-secondary flex items-center gap-2"><Download className="h-5 w-5" /> Download My Data</CardTitle>
              <CardDescription>Export your profiles, activities and reports as a JSON file</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleExport} disabled={exporting} data-testid="export-data-btn" variant="outline" className="rounded-full border-2 border-secondary text-secondary hover:bg-secondary hover:text-white">
                {exporting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing...</> : <><Download className="mr-2 h-4 w-4" /> Download JSON</>}
              </Button>
            </CardContent>
          </Card>

          {/* Delete account */}
          <Card className="rounded-3xl border-2 border-destructive/40 shadow-sm" data-testid="delete-account-card">
            <CardHeader>
              <CardTitle className="text-2xl text-destructive flex items-center gap-2"><Trash2 className="h-5 w-5" /> Delete Account</CardTitle>
              <CardDescription>This permanently deletes your account, child profiles, activities and reports. This cannot be undone.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleDelete} className="space-y-4">
                <div>
                  <Label htmlFor="delete-password" className="mb-2 block font-medium">Confirm with password</Label>
                  <Input id="delete-password" data-testid="delete-password-input" type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="h-12 rounded-xl border-2" placeholder="Leave blank if you signed up with Google" />
                </div>
                <div>
                  <Label htmlFor="delete-confirm" className="mb-2 block font-medium">Type <span className="font-bold text-destructive">DELETE</span> to confirm</Label>
                  <Input id="delete-confirm" data-testid="delete-confirm-input" type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="h-12 rounded-xl border-2" placeholder="DELETE" />
                </div>
                <Button type="submit" disabled={deleting || deleteConfirm !== "DELETE"} data-testid="delete-account-btn" className="rounded-full bg-destructive hover:bg-destructive/90 text-white">
                  {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...</> : "Delete My Account"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Account;
